import { matchPath, useLocation } from 'react-router-dom';

import { Routes } from 'src/constants/routes';

import { SearchParamKnownKeys, useUrlSearchParams } from 'src/hooks/useUrlSearchParams';

// Keep this list in sync with the URL parameters that SidePanelManager adds and clears.
const SIDE_PANEL_SEARCH_PARAM_KEYS = [
  SearchParamKnownKeys.panelType,
  SearchParamKnownKeys.panelObjectId,
  SearchParamKnownKeys.interviewModuleId,
  SearchParamKnownKeys.moduleMemberId,
] as const;

/**
 * How the dashboard renders the Agent dock for the current URL.
 *
 * The full-page Agent routes own the whole conversation surface, so the dock must not exist there
 * at all — not even as an off-screen animation target. A side panel only borrows the space, so the
 * dock stays mounted and slides away, ready to slide back when the panel closes.
 */
export const AGENT_DOCK_PRESENCE = {
  HIDDEN: 'HIDDEN',
  UNMOUNTED: 'UNMOUNTED',
  VISIBLE: 'VISIBLE',
} as const;
export type AgentDockPresence = (typeof AGENT_DOCK_PRESENCE)[keyof typeof AGENT_DOCK_PRESENCE];

/** Resolves the dock presence for a location; exported for tests. */
export const getAgentDockPresence = (pathname: string, urlSearchParams: URLSearchParams): AgentDockPresence => {
  const isAgentFullPageRoute = Boolean(
    matchPath(pathname, {
      exact: true,
      path: [Routes.AgentThread, Routes.Agent],
    })
  );
  if (isAgentFullPageRoute) {
    return AGENT_DOCK_PRESENCE.UNMOUNTED;
  }
  const isSidePanelOpen = SIDE_PANEL_SEARCH_PARAM_KEYS.some((key) => urlSearchParams.has(key));
  return isSidePanelOpen ? AGENT_DOCK_PRESENCE.HIDDEN : AGENT_DOCK_PRESENCE.VISIBLE;
};

export const useAgentDockPresence = (): AgentDockPresence => {
  const { pathname } = useLocation();
  const urlSearchParams = useUrlSearchParams();

  return getAgentDockPresence(pathname, urlSearchParams);
};
